import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEvent, createStore, sample } from "effector";
import { checkUpsellPurchased, UpsellStatus } from "@features/helpers";
import { COMPLETE_BUNDLE_UPSELL_ID, COMPLETE_BUNDLE_UPSELL_IDS, IUpsellContract } from "../model";
import { routePathNameChanged } from "@features/app/model";
import { interval, reset } from "patronum";
import { ISubscriptionContract } from "@features/subscription/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IUpsellUIContract } from ".";

// The drama / film bundles run the same screen and popup chain.
const TARGET_IDS = COMPLETE_BUNDLE_UPSELL_IDS;

const buyButtonClicked = createEvent();
const buyButtonDisabled = createEvent();
const buyButtonReset = createEvent();
// Two sequential intro popups: the "secret offer" teaser opens on arrival, its
// CTA reveals the discount popup, whose CTA finally leaves just the paywall.
const giftModalOpened = createEvent();
const giftModalClosed = createEvent();
const discountModalOpened = createEvent();
const discountModalClosed = createEvent();
const pollingStarted = createEvent();
const pollingStopped = createEvent();

// NOTE: these two stores are intentionally NOT reset on routePathNameChanged:
// the gift popup is opened from currentUpsellIdChanged, which fires BEFORE
// routePathNameChanged on the same navigation (routeStateChanged's useEffect in
// Providers.tsx runs first) — a route-change reset would instantly close it.
// Re-entry is normalized by giftModalOpened resetting the discount store.
const $isGiftModalOpen = createStore(false)
    .on(giftModalOpened, () => true)
    .on(giftModalClosed, () => false);
const $isDiscountModalOpen = createStore(false)
    .on(discountModalOpened, () => true)
    .on(discountModalClosed, () => false)
    .reset(giftModalOpened);
// Guards the intro popup chain against re-triggering while the user stays on
// the screen: `currentUpsellIdChanged` is re-emitted on every `$upsells`
// recompute (purchase-status rechecks, metadata refreshes), not only on real
// navigation. Set when the gift popup opens; cleared only when the user
// actually leaves the bundle page, so a later re-entry shows the chain again.
const $introShown = createStore(false).on(giftModalOpened, () => true);
const $upsellIds = createStore<string[]>([]);
const $isBuyButtonDisabled = createStore(true)
    .on(buyButtonClicked, () => true)
    .on(buyButtonDisabled, () => true)
    .on(buyButtonReset, () => false);

const { tick } = interval({
    timeout: 10000,
    start: pollingStarted,
    stop: pollingStopped,
    leading: false,
    trailing: false
});

const $completeBundleStatus = createStore<UpsellStatus | null>(null);
const checkRequested = createEvent();
const checkFinished = createEvent();
const $isCheckRequested = createStore(false).on(checkRequested, () => true);
const $isCheckPending = createStore(false)
    .on(checkRequested, () => true)
    .on(checkFinished, () => false);

const contract = new ModelContract(
    {
        $isGiftModalOpen,
        $isDiscountModalOpen,
        $completeBundleStatus,
        $upsellIds,
        $isBuyButtonDisabled
    },
    {
        buyButtonClicked,
        giftModalOpened,
        giftModalClosed,
        discountModalOpened,
        discountModalClosed
    },
    {},
    {}
);

export type ICompleteBundleContract = typeof contract;

export class CompleteBundleModule extends ModuleDescription<ICompleteBundleContract> {
    constructor(
        public authModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public paymentModule: IPaymentContract,
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public subscriptionsModule: ISubscriptionContract,
        public routesModule: IAppRouteUnitsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            source: { upsells: this.upsellModule.stores.$upsells },
            filter: (s) => s.upsells.length > 0,
            fn: (s) => s.upsells.filter((u) => TARGET_IDS.has(u.upsellId)).map((u) => u.upsellId),
            target: $upsellIds
        });

        sample({
            clock: buyButtonClicked,
            target: this.upsellModule.events.upsellBought
        });

        // Arrival on the bundle screen starts the popup chain: gift teaser first.
        sample({
            clock: this.upsellModule.events.currentUpsellIdChanged,
            source: { introShown: $introShown },
            filter: (s, id) => !s.introShown && id !== null && TARGET_IDS.has(id),
            target: giftModalOpened
        });

        // Leaving the bundle page re-arms the intro chain (routePathNameChanged
        // carries the new pathname and fires only on real navigation).
        sample({
            clock: routePathNameChanged,
            filter: (path) => !path.includes(COMPLETE_BUNDLE_UPSELL_ID),
            fn: () => false,
            target: $introShown
        });

        sample({
            clock: giftModalClosed,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null && TARGET_IDS.has(s.currentUpsellId),
            target: discountModalOpened
        });

        // Purchase status: drives the buy button state, the post-purchase redirect
        // outside the first-login funnel, and Processing polling. Same recipe as
        // the other pack modules.
        sample({
            source: {
                upsellIds: $upsellIds,
                upsellPath: this.routesModule.stores.upsell.$path,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckRequested: $isCheckRequested,
                isCheckPending: $isCheckPending
            },
            filter: (s) =>
                s.upsellIds.length > 0 &&
                s.upsellPath !== null &&
                s.upsellPath.includes(COMPLETE_BUNDLE_UPSELL_ID) &&
                ["solidgate", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckRequested &&
                !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: { upsellIds: $upsellIds, awaitingVerdict: this.upsellModule.stores.$upsellIdsAwaitingVerdict },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: (s, { result }) => {
                const statuses = s.upsellIds.map((upsellId) => checkUpsellPurchased(result, upsellId));

                if (statuses.includes("Success")) return "Success";
                if (statuses.includes("Processing")) return "Processing";
                // A just-bought pack in 3ds_verify reads as "Fail" until its bank
                // dialog is done; it is in progress — keep polling until the
                // webhook confirms instead of showing it as unpurchased.
                if (s.upsellIds.some((upsellId) => s.awaitingVerdict.includes(upsellId))) return "Processing";

                return "Fail";
            },
            target: $completeBundleStatus
        });

        sample({
            source: {
                status: $completeBundleStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) =>
                (s.status === "Success" || s.status === "Processing") &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes(COMPLETE_BUNDLE_UPSELL_ID) &&
                !s.firstLoginFlag,
            target: [this.upsellUiModule.events.packAccessRedirectTriggered, buyButtonDisabled]
        });

        sample({
            source: {
                status: $completeBundleStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path
            },
            filter: (s) =>
                s.status === "Fail" &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes(COMPLETE_BUNDLE_UPSELL_ID),
            target: buyButtonReset
        });

        sample({
            clock: $completeBundleStatus,
            source: { isUpsellRoute: this.routesModule.stores.upsell.$isActive },
            filter: (s, p) => !s.isUpsellRoute && p === "Processing",
            target: pollingStarted
        });

        sample({
            clock: $completeBundleStatus,
            filter: (status) => status === "Success" || status === "Fail",
            target: pollingStopped
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.finally,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: checkFinished
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.fail,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: [pollingStarted, buyButtonReset]
        });

        sample({
            clock: tick,
            source: { upsellIds: $upsellIds, isCheckPending: $isCheckPending },
            filter: (s) => s.upsellIds.length > 0 && !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        // $isCheckPending is reset on navigation for the same reason as in the
        // pack modules: this module's id list appears/disappears with the A/B
        // swap, so an in-flight check can end up unmatched by the `.finally`
        // filter and would otherwise block every later check.
        reset({
            clock: routePathNameChanged,
            target: [$completeBundleStatus, $isCheckRequested, $isCheckPending]
        });
    }
}
